import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { ACCEPT_INVITATION } from '../../../graphql/mutation'
import { VALIDATE_MEMBER_INVITATION } from '../../../graphql/query'

const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Last name is required'),
  password: Yup.string()
    .min(10, 'Password must be at least 10 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?=\S{8,})/,
      'Must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character without spaces'
    ),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: any) => val && val.length > 0,
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match")
    }),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions')
})

export const AcceptInvitation: React.FC = () => {
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const params = useParams()
  const uuid = params.uuid
  const [acceptInvitation] = useMutation(ACCEPT_INVITATION)
  const [loadingOL, setLoading] = useState(false)
  const userRef = useRef<any>(null)
  const navigate = useNavigate()

  const {
    loading: loadingValidation,
    error: errorValidation,
    data: dataValidation
  } = useQuery(VALIDATE_MEMBER_INVITATION, {
    variables: { input: { uuid } }
  })

  const initialValues = {
    firstname: '',
    lastname: '',
    email: dataValidation?.validateMemberInvitation?.data?.updatedUser?.email || '',
    password: '',
    changepassword: '',
    acceptTerms: false
  }

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      acceptInvitation({
        variables: {
          input: {
            name: `${values.firstname} ${values.lastname}`,
            id: userRef.current?.id,
            password: values.password
          }
        }
      })
        .then(() => {
          setLoading(false)
          navigate('/auth/login')
        })
        .catch((err) => {
          console.log(err)
          setLoading(false)
        })
    }
  })

  useEffect(() => {
    if (dataValidation?.validateMemberInvitation?.data?.updatedUser) {
      const { email } = dataValidation.validateMemberInvitation.data.updatedUser
      formik.setValues({
        ...formik.values,
        email: email || ''
      })
    }
  }, [dataValidation])

  if (loadingValidation) {
    return <div className='text-dark fs-4 text-center'>Please wait...</div>
  }

  if (errorValidation) {
    return (
      <>
        <h3>Oops! Error</h3>
      </>
    )
  }

  if (dataValidation) {
    if (dataValidation.validateMemberInvitation.status === 200) {
      userRef.current = dataValidation.validateMemberInvitation.data.updatedUser
      formik.initialValues.email = dataValidation.validateMemberInvitation.data.updatedUser.email

      if (dataValidation.validateMemberInvitation.data.updatedUser.status === 'Active') {
        return (
          <>
            <div className='text-dark fs-4 mb-8 fw-bold text-center'>
              You have already accepted this Invitation!
            </div>
            <div className='text-gray-400 fw-bolder fs-4 mt-8 text-center'>
              <Link
                to='/auth/login'
                className='link-primary fw-bolder'
                style={{ marginLeft: '5px' }}
              >
                Log In
              </Link>{' '}
              to continue
            </div>
          </>
        )
      }
    } else {
      return (
        <>
          <div className='text-dark fs-4 fw-bold text-center'>
            {dataValidation.validateMemberInvitation.message}
          </div>
        </>
      )
    }
  }

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='mb-10 text-center'>
        <h1 className='text-dark mb-3'>Accept Invitation</h1>
        <div className='text-gray-400 fw-bold fs-4'>We are excited to have you in botgo</div>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <div className='row fv-row'>
        <div className='col-xl-6'>
          <label className='form-label fw-bolder text-dark fs-6'>First name</label>
          <input
            placeholder='First name'
            contentEditable={false}
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('firstname')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.firstname && formik.errors.firstname
              },
              {
                'is-valid': formik.touched.firstname && !formik.errors.firstname
              }
            )}
          />
          {formik.touched.firstname && formik.errors.firstname && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.firstname}</span>
              </div>
            </div>
          )}
        </div>
        <div className='col-xl-6'>
          <div className='fv-row mb-5'>
            <label className='form-label fw-bolder text-dark fs-6'>Last name</label>
            <input
              placeholder='Last name'
              type='text'
              autoComplete='off'
              {...formik.getFieldProps('lastname')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.lastname && formik.errors.lastname
                },
                {
                  'is-valid': formik.touched.lastname && !formik.errors.lastname
                }
              )}
            />
            {formik.touched.lastname && formik.errors.lastname && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.lastname}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='fv-row mb-7'>
        <label className='form-label fw-bolder text-dark fs-6'>Email</label>
        <input
          placeholder='Email'
          type='email'
          disabled={!!userRef.current?.email}
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email
            }
          )}
        />
      </div>

      <div className='mb-10 fv-row'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>Password</label>
          <div className='position-relative mb-3 d-flex'>
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder='Password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.password && formik.errors.password
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password
                }
              )}
            />
            {/* <button
              className='btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2'
              type='button'
              onClick={toggleNewPasswordVisibility}
            >
              <i className={`bi ${showNewPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button> */}
            <div className='input-group-append'>
              <span
                className='form-control form-control-lg form-control-solid'
                id='togglePassword'
                onClick={toggleNewPasswordVisibility}
                style={{
                  cursor: 'pointer',
                  borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
                }}
              >
                {showNewPassword ? (
                  <i className='bi bi-eye-slash text-dark'></i>
                ) : (
                  <i className='bi bi-eye text-dark'></i>
                )}
              </span>
            </div>
          </div>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='mb-10 fv-row'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>Confirm Password</label>
          <div className='position-relative mb-3 d-flex'>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder='Confirm Password'
              autoComplete='off'
              {...formik.getFieldProps('changepassword')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.changepassword && formik.errors.changepassword
                },
                {
                  'is-valid': formik.touched.changepassword && !formik.errors.changepassword
                }
              )}
            />
            {/* <button
              className='btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2'
              type='button'
              onClick={toggleConfirmPasswordVisibility}
            >
              <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button> */}
            <div className='input-group-append'>
              <span
                className='form-control form-control-lg form-control-solid'
                id='togglePassword'
                onClick={toggleConfirmPasswordVisibility}
                style={{
                  cursor: 'pointer',
                  borderRadius: '0px 5px 5px 0px' // Adjust the border radius as needed
                }}
              >
                {showConfirmPassword ? (
                  <i className='bi bi-eye-slash text-dark'></i>
                ) : (
                  <i className='bi bi-eye text-dark'></i>
                )}
              </span>
            </div>
          </div>
        </div>
        {formik.touched.changepassword && formik.errors.changepassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.changepassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-10'>
        <label className='form-check form-check-custom form-check-solid form-check-inline'>
          <input
            className='form-check-input'
            type='checkbox'
            {...formik.getFieldProps('acceptTerms')}
          />
          <span className='form-check-label fw-bold text-gray-700 fs-6'>
            I Agree the
            <a href='#' className='ms-1 link-primary'>
              terms and conditions
            </a>
            .
          </span>
        </label>
        {formik.touched.acceptTerms && formik.errors.acceptTerms && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.acceptTerms}</span>
            </div>
          </div>
        )}
      </div>

      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_up_submit'
          className='btn btn-lg btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || loadingOL}
        >
          {!loadingOL && <span className='indicator-label'>Submit</span>}
          {loadingOL && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}
